import React from 'react';
import {
	ArrayInput,
	ArrayField,
	ChipField,
	Create,
	Datagrid,
	Edit,
	EditButton,
	FormTab,
	FunctionField,
	ImageInput,
	ImageField,
	List,
	ReferenceInput,
	ReferenceField,
	SelectInput,
	SelectField,
	SimpleForm,
	SimpleFormIterator,
	SingleFieldList,
	TabbedForm,
	TextInput,
	TextField,
	UrlField,
	Filter, ReferenceManyField, useRecordContext, usePermissions, SaveButton,
} from 'react-admin';
import { RichTextInput } from 'ra-input-rich-text';
// eslint-disable-next-line import/no-extraneous-dependencies
import { Toolbar } from 'ra-ui-materialui';
import { Grid } from '@mui/material';
import {
	usePermissionsExt,
	withMasterPermissions,
	WithMasterPermissions,
} from '../../DataProviderExtension/authExtension';
import {
	BusinessReviewTab,
	BusinessPOSFormTab,
	LocationInput,
	BusinessPromotionTab,
	catalogStatus,
	versionStatus,
} from './shared';
import BusinessAside from '../BusinessAside';
import UserProfileField from '../../components/UserProfileField';
import CreateInDialogButton from '../../components/dialogs/CreateInDialogButton';

const VERSION_STATUS_EDITING = 'editing';
const VERSION_STATUS_MODERATING = 'moderating';
const VERSION_STATUS_PUBLISHED = 'published';

const styles = {
	sizedImage: {
		width: '15rem',
		'& img': { width: 'inherit' },
	},
};

const SendToModerationButton = () => {
	const transform = (data) => {
		return { ...data, versionStatus: VERSION_STATUS_MODERATING };
	};
	return (
		<SaveButton
			type="button"
			label="На модерацию"
			alwaysEnable
			transform={transform}
		/>
	);
};

const PublishButton = () => {
	const transform = (data) => {
		return { ...data, versionStatus: VERSION_STATUS_PUBLISHED };
	};
	return (
		<SaveButton
			type="button"
			label="Одобрить"
			alwaysEnable
			transform={transform}
		/>
	);
};

const RejectToEditButton = () => {
	const transform = (data) => {
		return { ...data, versionStatus: VERSION_STATUS_EDITING };
	};
	return (
		<SaveButton
			type="button"
			label="Отклонить"
			alwaysEnable
			transform={transform}
		/>
	);
};

const FormToolBar = () => {
	const {
		isLoading: isPermissionsLoading, master, businessAdmin, businessAdminIds,
	} = usePermissionsExt();
	const record = useRecordContext();
	if (isPermissionsLoading) {
		return null;
	}
	if (master) {
		if (!record.mainVersion) {
			return <Toolbar />;
		}
		if (record.versionStatus === VERSION_STATUS_MODERATING) {
			return (
				<Toolbar>
					<PublishButton />
					<RejectToEditButton />
				</Toolbar>
			);
		}
		return null;
	}

	if (businessAdmin && businessAdminIds.includes(record.mainVersion?.id)) {
		if (record.versionStatus === 'editing') {
			return (
				<Toolbar>
					<SaveButton />
					<SendToModerationButton />
				</Toolbar>
			);
		}
		return <Toolbar />;
	}

	return null;
};

const CreateBusinessAdminForm = () => {
	return (
		<SimpleForm>
			<TextInput label="id пользователя" source="parseUser.id" />
		</SimpleForm>
	);
};

const CreateBusinessAdminButton = (props) => {
	const { children } = props;
	const record = useRecordContext();
	return (
		<CreateInDialogButton
			fullWidth
			record={{
				business: { id: record.id },
			}}
			resource="BusinessAdmin"
		>
			{children}
		</CreateInDialogButton>
	);
};


const BusinessAdminsTab = (props) => {
	return (
		<FormTab {...props} label="Администраторы">
			<CreateBusinessAdminButton>
				<CreateBusinessAdminForm />
			</CreateBusinessAdminButton>
			<ReferenceManyField reference="BusinessAdmin" target="business">
				<Datagrid>
					<UserProfileField label="Пользователь" source="parseUser.id" />
				</Datagrid>
			</ReferenceManyField>
		</FormTab>
	);
};

const BusinessMainFormTab = (props) => {
	return (
		<FormTab {...props} label="Основное">
			<Grid container spacing={2}>
				<Grid item xs={12} sm={3} lg={2}>
					<TextInput label="Название" source="title.short" fullWidth />
				</Grid>
				<Grid item xs={12} sm={9} lg={6}>
					<TextInput label="Сфера деятельности" source="title.occupation" fullWidth />
				</Grid>
				<Grid item xs={12} sm={6} lg={2}>
					<ReferenceInput label="Категория" source="category.id" reference="Category">
						<SelectInput optionText="title" fullWidth />
					</ReferenceInput>
				</Grid>
				<Grid item xs={12} sm={6} lg={2}>
					<TextInput label="Логотип" source="logo" fullWidth />
				</Grid>
				<Grid item xs={12} sm={6}>
					<ImageInput source="logoFile" label="Related pictures" accept="image/*">
						<ImageField sx={styles.sizedImage} source="src" title="title" fullWidth />
					</ImageInput>
				</Grid>
				<Grid item xs={12} sm={6}>
					<TextInput label="Краткое предложение" source="offer.short" fullWidth />
				</Grid>
				<Grid item xs={12} lg={6}>
					<RichTextInput label="Описание" source="about" fullWidth />
				</Grid>
				<Grid item xs={12} lg={6}>
					<RichTextInput label="Развернутое предложение" source="offer.full" fullWidth />
				</Grid>
				<Grid item xs={12} sm={6} lg={2}>
					<TextInput label="Сайт (на форме)" source="site.show" fullWidth />
				</Grid>
				<Grid item xs={12} sm={6} lg={2}>
					<TextInput label="Сайт (для перехода)" source="site.go" fullWidth />
				</Grid>
				<Grid item xs={12} sm={6} lg={4}>
					<ArrayInput label="Телефоны" source="phones" fullWidth>
						<SimpleFormIterator>
							<TextInput label="На форме" source="show" />
							<TextInput label="Для набора" source="dial" />
						</SimpleFormIterator>
					</ArrayInput>
				</Grid>
				<Grid item xs={12} sm={6} lg={4}>
					<ArrayInput label="Электронная почта" source="emails" fullWidth>
						<SimpleFormIterator>
							<TextInput label="eMail" source="address" />
						</SimpleFormIterator>
					</ArrayInput>
				</Grid>
				<Grid item xs={12} sm={6} lg={4}>
					<TextInput label="Адрес (на форме)" source="address.show" fullWidth />
				</Grid>
				<Grid item xs={12} sm={6} lg={4}>
					<TextInput label="Адрес (для поиска)" source="address.nav" fullWidth />
				</Grid>
				<Grid item xs={12} sm={6} lg={2}>
					<TextInput label="Широта" source="address.lat" fullWidth />
				</Grid>
				<Grid item xs={12} sm={6} lg={2}>
					<TextInput label="Долгота" source="address.lon" fullWidth />
				</Grid>
				<Grid item xs={12}>
					<LocationInput label="Локации" source="address.locationIds" fullWidth />
				</Grid>
				<Grid item xs={12} sm={6} lg={2}>
					<TextInput label="VK" source="social.vk" fullWidth />
				</Grid>
				<Grid item xs={12} sm={6} lg={2}>
					<TextInput label="Instagram" source="social.inst" fullWidth />
				</Grid>
				<Grid item xs={12} sm={6} lg={2}>
					<TextInput label="Facebook" source="social.fb" fullWidth />
				</Grid>
				<Grid item xs={12} sm={6} lg={2}>
					<TextInput label="Youtube" source="social.yt" fullWidth />
				</Grid>
				<Grid item xs={12} sm={6} lg={2}>
					<TextInput label="OK" source="social.ok" fullWidth />
				</Grid>
				<Grid item xs={12} sm={6} lg={2}>
					<TextInput label="Telegram" source="social.tg" fullWidth />
				</Grid>
				<Grid item xs={12} sm={6} lg={2}>
					<SelectInput label="Статус" source="catalogStatus" choices={catalogStatus} defaultValue="active" fullWidth />
				</Grid>
			</Grid>
		</FormTab>
	);
};

const versionTitle = (r) => (r.mainVersion?.id ? 'Черновик' : 'Основная');

const EditBusinessTitle = () => {
	const record = useRecordContext();
	if (!record) {
		return null;
	}
	const titleComponents = [];
	titleComponents.push(record.title.short);
	titleComponents.push(versionTitle(record));
	return `${titleComponents.filter((c) => c !== undefined).join(' ')}`;
};

export const EditBusiness = (props) => {
	const { isLoading, permissions } = usePermissions();
	if (isLoading) {
		return null;
	}
	return (
		<Edit
			aside={<BusinessAside />}
			title={<EditBusinessTitle />}
			transform={({ social, ...rest }) => {
				const result = { ...rest, social: {} };
				Object.keys(social).forEach((k) => { if (social[k]) { result.social[k] = social[k]; } });
				return result;
			}}
		>
			<TabbedForm toolbar={<FormToolBar />}>
				<BusinessMainFormTab {...props} />
				<BusinessPOSFormTab {...props} />
				<BusinessPromotionTab {...props} />
				<BusinessReviewTab {...props} />
				{
					withMasterPermissions(permissions, <BusinessAdminsTab {...props} />)
				}
			</TabbedForm>
		</Edit>
	);
};

export const createBusiness = (props) => {
	return (
		<Create {...props}>
			<TabbedForm>
				<BusinessMainFormTab {...props} />
				<BusinessPOSFormTab {...props} />
			</TabbedForm>
		</Create>
	);
};

const BusinessFilter = (props) => (
	<Filter {...props}>
		<TextInput
			label="Название"
			source="title.short"
			alwaysOn
		/>
	</Filter>
);

export const ListBusiness = (props) => {
	const {
		isLoading: isPermissionsLoading,
		permissions,
		businessAdminIds,
		businessAdmin,
		master,
	} = usePermissionsExt();
	if (isPermissionsLoading) {
		return null;
	}
	const filter = (!master && businessAdmin)
		? {
			'': {
				$or: [
					...businessAdminIds.map((i) => ({ mainVersion: { id: i } })),
				],
			},
		}
		: undefined;
	return (
		<List
			{...props}
			title="Партнеры"
			sort={{ field: 'title.short', order: 'ASC' }}
			filters={<BusinessFilter />}
			filter={filter}
		>
			<Datagrid
				rowClick={(id, resource, r) => (
					master || (businessAdmin && businessAdminIds.includes(r.mainVersion?.id))
						? 'edit'
						: false
				)}
				bulkActionButtons={withMasterPermissions(permissions, undefined, false)}
			>
				<TextField label="Название" source="title.short" />
				<SelectField label="Статус" source="catalogStatus" choices={catalogStatus} />
				<TextField label="Сфера деятельности" source="title.occupation" />
				<ReferenceField label="Категория" source="category.id" reference="Category">
					<TextField source="title" />
				</ReferenceField>
				<TextField label="Краткое предложение" source="offer.short" />
				<UrlField label="Сайт (для перехода)" source="site.go" />
				<ArrayField source="phones">
					<SingleFieldList>
						<ChipField source="dial" />
					</SingleFieldList>
				</ArrayField>
				<FunctionField
					label="Версия"
					render={versionTitle}
				/>
				<SelectField label="Статус" source="versionStatus" choices={versionStatus} />
				<WithMasterPermissions>
					<EditButton />
				</WithMasterPermissions>
			</Datagrid>
		</List>
	);
};
